import '@askable/ui/styles/globals.css';

import { Toaster } from '@askable/ui/core/sonner';
import { TooltipProvider } from '@askable/ui/core/tooltip';
import { datadogRum } from '@datadog/browser-rum';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';

import { isImpersonating } from 'utils/impersonation';

import { AppThemeProvider } from 'components/AppThemeProvider/AppThemeProvider';
import { ErrorBoundaryApp } from 'components/ErrorBoundaryApp/ErrorBoundaryApp';

import i18n, { i18nReady } from './i18n';
import Routes from './router';

import './styles/legacy-tokens.css';
import './styles/scrollBarStyles.scss';
import './styles/styles.scss';

const IS_PROD = import.meta.env.VITE_ENVIRONMENT === 'PROD';

if (IS_PROD) {
  datadogRum.init({
    applicationId: import.meta.env.VITE_DD_APPLICATION_ID,
    clientToken: import.meta.env.VITE_DD_CLIENT_TOKEN,
    site: 'us3.datadoghq.com',
    service: 'clients',
    env: 'production',
    version: import.meta.env.VITE_APP_VERSION,
    sessionSampleRate: 100,
    // Recording starts only when an error boundary calls startSessionReplayRecording().
    // There is no retroactive buffer, so replays begin at the crash. Sessions that
    // never record are not billed.
    sessionReplaySampleRate: 100,
    startSessionReplayRecordingManually: true,
    traceContextInjection: 'sampled',
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [{ match: import.meta.env.VITE_GRAPHQL_URL, propagatorTypes: ['tracecontext'] }],
    beforeSend: () => {
      if (isImpersonating()) {
        return false;
      }

      return true;
    },
  });
}

const container = document.getElementById('root');
const root = createRoot(container!);

// The first render waits for the active locale, so a non-English user never sees English text.
void i18nReady.then(() => {
  root.render(
    <ErrorBoundaryApp>
      <HelmetProvider>
        <I18nextProvider i18n={i18n}>
          <AppThemeProvider>
            <TooltipProvider>
              <Routes />
            </TooltipProvider>
          </AppThemeProvider>
          <Toaster />
        </I18nextProvider>
      </HelmetProvider>
    </ErrorBoundaryApp>,
  );
});
